import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { reportError } from '@/lib/report'

type State = { hasError: boolean; error: unknown }

// Fallback lives in a function component so it can useTranslation — class
// components can't call hooks. onRetry flips the boundary back to children.
function ErrorFallback({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation()
  const msg = error instanceof Error ? error.message : String(error)
  // ApiError/GatewayApiError carry the gateway's request id — surfacing it
  // lets a dogfood report join straight to analytics_inference_requests.
  const requestId =
    typeof error === 'object' && error !== null && 'requestId' in error
      ? String((error as { requestId?: unknown }).requestId ?? '')
      : ''
  return (
    <div className="mx-auto max-w-2xl p-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('common.errorTitle')}</CardTitle>
          <CardDescription className="break-all">{msg}</CardDescription>
          {requestId ? (
            <CardDescription className="break-all font-mono text-xs">
              {t('common.errorReportId', { id: requestId })}
            </CardDescription>
          ) : null}
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button onClick={onRetry}>{t('common.retry')}</Button>
          <Button variant="outline" onClick={() => location.reload()}>
            {t('common.reload')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, error: null }
  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error }
  }
  componentDidCatch(error: unknown, info: unknown) {
    console.error('ErrorBoundary', error, info)
    // Dogfood beacon: a render crash the route boundary catches is exactly
    // the failure class the CP can't see — report it (fire-and-forget).
    const componentStack =
      typeof info === 'object' && info !== null && 'componentStack' in info
        ? String((info as { componentStack?: unknown }).componentStack ?? '')
        : ''
    reportError(error, 'error_boundary', {
      extra: { componentStack: componentStack.slice(0, 1000) },
    })
  }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <ErrorFallback
        error={this.state.error}
        onRetry={() => this.setState({ hasError: false, error: null })}
      />
    )
  }
}

// Per-route boundary: keying the class on pathname remounts it on navigation,
// resetting hasError so one crashed route can't wedge the whole console.
// Same-path ?search navigations keep the boundary (and its state) — the Retry
// button covers that case.
export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
}
